import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, RefreshCw, AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { useAccess } from "@/context/AccessContext";

const MAX_POLLS = 8;

function paidMessage(result) {
  const via = result.provider === "paypal" ? " via PayPal" : "";
  if (result.plan === "oracle_pass_30d") return "Your 30-day pass is active on this browser — sign in to carry it across devices. Go ask the numbers.";
  if (!result.trial) return `Your subscription is active${via}. Go ask the numbers.`;
  const firstPayment = result.trial_end ? ` — first payment on ${new Date(result.trial_end).toLocaleDateString(undefined, { month: "short", day: "numeric" })}` : "";
  return `Your free trial has started${firstPayment}${via}. Cancel anytime from your profile.`;
}

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refresh } = useAccess();
  const [state, setState] = useState("checking");
  const [result, setResult] = useState({});
  const attempts = useRef(0);

  useEffect(() => {
    const sessionId = params.get("session_id");
    const paypal = params.get("provider") === "paypal";
    // PayPal appends subscription_id (subscriptions) or token (orders) to the return URL
    const ppKind = params.get("kind") === "order" ? "order" : "subscription";
    const ppId = ppKind === "order" ? params.get("token") : params.get("subscription_id");
    if (!sessionId && !(paypal && ppId)) { setState("error"); return; }
    let timer;
    const poll = async () => {
      try {
        const { data } = paypal
          ? await api.post("/paypal/complete", { kind: ppKind, id: ppId })
          : await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          const acc = await refresh(true);
          setResult({ trial: data.trial, plan: data.lookup_key, trial_end: acc?.trial_end, kind: acc?.kind, provider: data.provider || "stripe" });
          setState("paid");
          return;
        }
        if (data.status === "expired" || data.payment_status === "failed") { setState("error"); return; }
      } catch {
        setState("error");
        return;
      }
      attempts.current += 1;
      if (attempts.current >= MAX_POLLS) { setState("timeout"); return; }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [params, refresh]);

  return (
    <div className="min-h-screen bg-ink text-parchment flex items-center justify-center px-4">
      <div data-testid="payment-success-card" className="max-w-md w-full text-center bg-black/40 backdrop-blur-xl border border-oracle/30 rounded-xl p-10">
        {state === "checking" && (
          <>
            <RefreshCw className="w-10 h-10 mx-auto text-oracle animate-spin" strokeWidth={1.5} />
            <h1 className="font-heading text-3xl mt-6">Confirming your offering…</h1>
            <p data-testid="payment-status-text" className="font-body text-sm text-parchment/60 mt-3">Waiting for Stripe to confirm the payment.</p>
          </>
        )}
        {state === "paid" && (
          <>
            <CheckCircle2 className="w-12 h-12 mx-auto text-oracle" strokeWidth={1.5} />
            <h1 className="font-heading text-3xl mt-6">The Oracle is <span className="italic text-oracle">unlocked</span></h1>
            <p data-testid="payment-status-text" className="font-body text-sm text-parchment/60 mt-3">{paidMessage(result)}</p>
            <Button data-testid="go-to-oracle-btn" onClick={() => navigate("/")} className="mt-8 bg-oracle text-ink hover:bg-[#FFC033] label-caps px-8 py-5">Enter the Oracle</Button>
          </>
        )}
        {(state === "timeout" || state === "error") && (
          <>
            <AlertTriangle className="w-12 h-12 mx-auto text-pb_red" strokeWidth={1.5} />
            <h1 className="font-heading text-3xl mt-6">{state === "timeout" ? "Still confirming" : "Payment not confirmed"}</h1>
            <p data-testid="payment-status-text" className="font-body text-sm text-parchment/60 mt-3">
              {state === "timeout"
                ? "Stripe is taking longer than usual. Your subscription will activate automatically once confirmed — check back in a minute."
                : "We couldn't verify this payment. If you were charged, contact support with your session ID."}
            </p>
            <Button data-testid="back-home-btn" onClick={() => navigate("/")} variant="ghost" className="mt-8 text-parchment hover:text-oracle label-caps">Back to app</Button>
          </>
        )}
      </div>
    </div>
  );
}
